import React, { useState, useEffect } from 'react';
import {
    Button, Card, message, Typography, Space, Modal, Tag, Form, Input, Table, Switch, Popconfirm, Alert, Select
} from 'antd';
import {
    AlertOutlined, PlayCircleOutlined, EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined
} from '@ant-design/icons';
import {
    rulesService, RuleDto, LoaiTbDto, MucDoDto,
    CreateRuleRequest, UpdateRuleRequest
} from '../services/rulesService';
import { notificationsService } from '../services/notificationsService';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

type RuleFormState = {
    open: boolean;
    isEdit: boolean;
    editingId: number | null;
    values: {
        ruleName?: string;
        sqlQuery?: string;
        maloaitb?: number;
        mamucdo?: number;
        active?: boolean;
    };
};

const RuleCheckTab: React.FC = () => {
    const [rules, setRules] = useState<RuleDto[]>([]);
    const [rulesLoading, setRulesLoading] = useState(false);
    const [runningId, setRunningId] = useState<number | null>(null);
    const [runAllLoading, setRunAllLoading] = useState(false);
    const [deleteAllLoading, setDeleteAllLoading] = useState(false);

    const [loaiTbList, setLoaiTbList] = useState<LoaiTbDto[]>([]);
    const [mucDoList, setMucDoList] = useState<MucDoDto[]>([]);
    const [dmLoading, setDmLoading] = useState(false);

    const [formModal, setFormModal] = useState<RuleFormState>({
        open: false,
        isEdit: false,
        editingId: null,
        values: { active: true }
    });
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (rules.length === 0) loadRules();
        if (loaiTbList.length === 0) loadDanhMuc();
    }, []);

    const loadRules = async () => {
        setRulesLoading(true);
        try {
            const list = await rulesService.getAll();
            setRules(list);
        } catch (error: any) {
            console.error('Load rules error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Không thể tải danh sách rule';
            message.error(msg);
        } finally {
            setRulesLoading(false);
        }
    };

    const loadDanhMuc = async () => {
        setDmLoading(true);
        try {
            const [loaitb, mucdo] = await Promise.all([
                rulesService.getLoaiTb(),
                rulesService.getMucDo()
            ]);
            setLoaiTbList(loaitb);
            setMucDoList(mucdo);
        } catch (error: any) {
            console.error('Load danh mục rule error:', error);
        } finally {
            setDmLoading(false);
        }
    };

    const openCreateRule = () => {
        setFormModal({
            open: true,
            isEdit: false,
            editingId: null,
            values: { ruleName: '', sqlQuery: '', maloaitb: undefined, mamucdo: undefined, active: true }
        });
        form.setFieldsValue({ ruleName: '', sqlQuery: '', maloaitb: undefined, mamucdo: undefined, active: true });
    };

    const openEditRule = (r: RuleDto) => {
        setFormModal({
            open: true,
            isEdit: true,
            editingId: r.id,
            values: {
                ruleName: r.ruleName,
                sqlQuery: r.sqlQuery,
                maloaitb: r.maloaitb,
                mamucdo: r.mamucdo,
                active: (r.active ?? 0) === 1
            }
        });
        form.setFieldsValue({
            ruleName: r.ruleName,
            sqlQuery: r.sqlQuery,
            maloaitb: r.maloaitb,
            mamucdo: r.mamucdo,
            active: (r.active ?? 0) === 1
        });
    };

    const closeFormModal = () => {
        setFormModal({ open: false, isEdit: false, editingId: null, values: { active: true } });
        form.resetFields();
    };

    const submitFormRule = async () => {
        try {
            const values = await form.validateFields();
            setFormSubmitting(true);

            if (formModal.isEdit && formModal.editingId != null) {
                const payload: UpdateRuleRequest = {
                    ruleName: String(values.ruleName ?? '').trim(),
                    sqlQuery: String(values.sqlQuery ?? '').trim(),
                    maloaitb: Number(values.maloaitb),
                    mamucdo: Number(values.mamucdo),
                    active: values.active ? 1 : 0
                };
                const res = await rulesService.update(formModal.editingId, payload);
                message.success(res.message || 'Cập nhật rule thành công');
            } else {
                const payload: CreateRuleRequest = {
                    ruleName: String(values.ruleName ?? '').trim(),
                    sqlQuery: String(values.sqlQuery ?? '').trim(),
                    maloaitb: Number(values.maloaitb),
                    mamucdo: Number(values.mamucdo),
                    active: values.active ? 1 : 0
                };
                const res = await rulesService.create(payload);
                message.success(res.message || `Tạo rule thành công (id=${res.id})`);
            }
            closeFormModal();
            await loadRules();
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            console.error('Submit rule form error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Lưu rule thất bại';
            message.error(msg);
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleDeleteRule = async (r: RuleDto) => {
        try {
            const res = await rulesService.remove(r.id);
            message.success(res.message || 'Xóa rule thành công');
            setRules(prev => prev.filter(x => x.id !== r.id));
        } catch (error: any) {
            console.error('Delete rule error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Xóa rule thất bại';
            message.error(msg);
        }
    };

    const handleToggleActive = async (r: RuleDto, checked: boolean) => {
        try {
            const res = await rulesService.toggleActive(r.id, checked);
            message.success(res.message);
            setRules(prev => prev.map(x => x.id === r.id ? { ...x, active: checked ? 1 : 0 } : x));
        } catch (error: any) {
            console.error('Toggle rule active error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Cập nhật trạng thái thất bại';
            message.error(msg);
        }
    };

    const handleRunRule = async (r: RuleDto) => {
        setRunningId(r.id);
        try {
            const res = await rulesService.run(r.id);
            console.log('Rule response:', res);
            if (res.success) {
                const inserted = res.notificationsInserted ?? 0;
                const returned = res.rowsReturned ?? 0;
                console.log(`About to show message: Chạy rule [${r.ruleName}] thành công: HIS trả về ${returned} dòng → Đã chèn ${inserted} cảnh báo mới`);

                const toast = document.createElement('div');
                toast.style.cssText = `
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: #52c41a;
                    color: white;
                    padding: 16px 24px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    z-index: 9999;
                    font-size: 14px;
                    max-width: 400px;
                    animation: slideIn 0.3s ease-out;
                `;
                toast.innerHTML = `
                    <div style="font-weight: bold; margin-bottom: 4px;">Chạy rule [${r.ruleName}] thành công</div>
                    <div style="font-size: 12px;">HIS trả về ${returned} dòng → Đã chèn ${inserted} cảnh báo mới</div>
                `;
                document.body.appendChild(toast);

                const style = document.createElement('style');
                style.textContent = `
                    @keyframes slideIn {
                        from { transform: translateX(100%); opacity: 0; }
                        to { transform: translateX(0); opacity: 1; }
                    }
                `;
                document.head.appendChild(style);

                setTimeout(() => {
                    toast.style.animation = 'slideIn 0.3s ease-out reverse';
                    setTimeout(() => {
                        toast.remove();
                        style.remove();
                    }, 300);
                }, 5000);
            } else {
                message.error(res.message || `Chạy rule [${r.ruleName}] thất bại`);
            }
        } catch (error: any) {
            console.groupCollapsed('%c[Rule Run] LỖI 500 — xem chi tiết bên trong:', 'color:#fff;background:#c0392b;padding:2px 8px;border-radius:4px;');
            console.log('error.message =', error?.message);
            console.log('error.response.status =', error?.response?.status);
            const data = error?.response?.data;
            console.log('error.response.data (RunRuleResponse từ BE) =', data);
            if (data) {
                console.log('  → success:', data.success ?? data.Success);
                console.log('  → ruleId:', data.ruleId ?? data.RuleId);
                console.log('  → ruleName:', data.ruleName ?? data.RuleName);
                console.log('  → rowsReturned:', data.rowsReturned ?? data.RowsReturned);
                console.log('  → notificationsInserted:', data.notificationsInserted ?? data.NotificationsInserted);
                console.log('  → Message (Exception thật):', data.message ?? data.Message);
            }
            console.error('Stack gốc nếu có:', error);
            console.groupEnd();

            const msgBody = data ? (data.message || data.Message) : null;
            const msg = msgBody || error?.message || 'Chạy rule thất bại (lỗi server — mở Console DevTools để xem chi tiết StackTrace thật)';
            message.error(msg);
        } finally {
            setRunningId(null);
        }
    };

    const handleRunAllActive = async () => {
        const countActive = rules.filter(r => (r.active ?? 0) === 1).length;
        if (countActive === 0) {
            message.warning('Không có rule nào đang active để chạy.');
            return;
        }
        setRunAllLoading(true);
        try {
            const res = await rulesService.runActive();
            if (res.success) {
                const totalReturned = res.totalRowsReturned ?? 0;
                const totalInserted = res.totalNotificationsInserted ?? 0;
                message.success(`Đã chạy ${countActive} rule active thành công: HIS trả về ${totalReturned} dòng → Đã chèn ${totalInserted} cảnh báo mới`, 5);
            } else {
                message.error(res.message || 'Một vài rule chạy thất bại');
            }
            const failed = (res.details || []).filter(d => !d.success);
            if (failed.length > 0) {
                console.warn('Run-all có rule lỗi:', failed);
            }
        } catch (error: any) {
            console.error('Run all active rules error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Chạy rule thất bại (lỗi server)';
            message.error(msg);
        } finally {
            setRunAllLoading(false);
        }
    };

    const handleDeleteAllNotifications = async () => {
        setDeleteAllLoading(true);
        try {
            const result = await notificationsService.deleteAll();
            if (result.success) {
                message.success(`Đã xóa ${result.deletedCount} cảnh báo khỏi bảng notifications`);
            } else {
                message.error(result.message || 'Xóa bảng cảnh báo thất bại');
            }
        } catch (error: any) {
            console.error('Delete all notifications error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Xóa bảng cảnh báo thất bại';
            message.error(msg);
        } finally {
            setDeleteAllLoading(false);
        }
    };

    const tagColorForMucDo = (colorCode?: string) => {
        if (!colorCode) return 'default';
        return colorCode as any;
    };

    const ruleColumns: any[] = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            render: (v: any) => <Text code>{v}</Text>
        },
        {
            title: 'Tên Rule',
            dataIndex: 'ruleName',
            key: 'ruleName',
            render: (v: any) => <Text strong>{v}</Text>
        },
        {
            title: 'Loại TB',
            dataIndex: 'tenLoaiTb',
            key: 'tenLoaiTb',
            width: 160,
            render: (_: any, r: RuleDto) => (
                <Tag color="blue">{r.tenLoaiTb || `Mã ${r.maloaitb}`}</Tag>
            )
        },
        {
            title: 'Mức độ',
            dataIndex: 'tenMucDo',
            key: 'tenMucDo',
            width: 160,
            render: (_: any, r: RuleDto) => (
                <Tag color={tagColorForMucDo(r.colorCode)}>
                    {r.tenMucDo || `Mã ${r.mamucdo}`}
                </Tag>
            )
        },
        {
            title: 'SQL truy vấn HIS (preview)',
            dataIndex: 'sqlQuery',
            key: 'sqlQuery',
            ellipsis: true,
            render: (v: string) => (
                <Text code type="secondary" style={{ whiteSpace: 'nowrap', maxWidth: 380, display: 'inline-block' }}>
                    {(v || '').replace(/\s+/g, ' ').slice(0, 120)}
                    {(v || '').length > 120 ? ' …' : ''}
                </Text>
            )
        },
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            width: 90,
            align: 'center' as any,
            render: (_: any, r: RuleDto) => (
                <Switch
                    checked={(r.active ?? 0) === 1}
                    onChange={(ch) => handleToggleActive(r, ch)}
                    size="small"
                />
            )
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 240,
            render: (_: any, r: RuleDto) => (
                <Space size={6} wrap>
                    <Button
                        type="primary"
                        size="small"
                        icon={<PlayCircleOutlined />}
                        onClick={() => handleRunRule(r)}
                        loading={runningId === r.id}
                        style={{ backgroundColor: '#1890ff' }}
                    >
                        Chạy
                    </Button>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEditRule(r)}
                    >
                        Sửa
                    </Button>
                    <Popconfirm
                        title="Xóa rule này?"
                        description={`Rule "${r.ruleName}" sẽ bị xóa vĩnh viễn.`}
                        okText="Xóa"
                        okType="danger"
                        cancelText="Hủy"
                        onConfirm={() => handleDeleteRule(r)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />}>Xóa</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Space align="start" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <Space direction="vertical" size={4}>
                            <Title level={4} style={{ margin: 0 }}>
                                <AlertOutlined style={{ marginRight: 8, color: '#eb2f96' }} />
                                Danh sách Rule Check Cảnh báo
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                Các rule chạy câu SELECT trên CSDL HIS (ReportDB). Kết quả mỗi dòng truy vấn được insert vào bảng <Text code>notifications</Text> của Portal theo mapping cột trùng tên: <Text code>makcb, manv, makk, noidung, ngay, tendichvu, manhom, ngaykt</Text>. Cột <Text code>rule_id, maloaitb, mamucdo</Text> tự điền theo rule.
                            </Text>
                        </Space>
                        <Space wrap>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadRules}
                                loading={rulesLoading}
                            >
                                Làm mới
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={handleRunAllActive}
                                loading={runAllLoading}
                                style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
                            >
                                Chạy tất cả (Active)
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openCreateRule}
                                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                            >
                                Thêm rule mới
                            </Button>
                            <Popconfirm
                                title="Xóa toàn bộ bảng cảnh báo?"
                                description="Hành động này sẽ xóa TẤT CẢ dữ liệu trong bảng notifications. Không thể hoàn tác sau khi xóa."
                                okText="Xóa tất cả"
                                okType="danger"
                                cancelText="Hủy"
                                onConfirm={handleDeleteAllNotifications}
                            >
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deleteAllLoading}
                                >
                                    Xóa bảng TB
                                </Button>
                            </Popconfirm>
                        </Space>
                    </Space>

                    <Alert
                        type="info"
                        showIcon
                        message="Chỉ chấp nhận READ-ONLY SQL"
                        description={
                            <div style={{ fontSize: 13 }}>
                                Câu truy vấn trên HIS phải bắt đầu bằng <Text code>DECLARE</Text> (khai báo biến), <Text code>SELECT</Text> hoặc <Text code>WITH</Text> (CTE). Các lệnh <Text code>INSERT/UPDATE/DELETE/DROP/ALTER</Text> sẽ bị từ chối ở Backend để đảm bảo an toàn cho CSDL HIS. Các cột trong SELECT nên dùng alias trùng tên bảng <Text code>notifications</Text>: <Text code>makcb, manv, makk, noidung, ngay, tendichvu, manhom, ngaykt</Text>.
                            </div>
                        }
                    />

                    <Table<RuleDto>
                        rowKey="id"
                        size="middle"
                        loading={rulesLoading}
                        dataSource={rules}
                        columns={ruleColumns}
                        scroll={{ x: 1200 }}
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `Tổng ${total} rule`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            defaultPageSize: 20
                        }}
                    />
                </Space>
            </Card>

            <Modal
                open={formModal.open}
                title={
                    <Space>
                        {formModal.isEdit ? <EditOutlined /> : <PlusOutlined />}
                        {formModal.isEdit ? 'Chỉnh sửa Rule' : 'Tạo Rule mới'}
                    </Space>
                }
                width={820}
                okText="Lưu"
                cancelText="Hủy"
                confirmLoading={formSubmitting}
                onCancel={closeFormModal}
                onOk={submitFormRule}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ active: true }}
                >
                    <Form.Item
                        label="Tên rule"
                        name="ruleName"
                        rules={[
                            { required: true, message: 'Vui lòng nhập tên rule' },
                            { max: 500, message: 'Tên quá dài (tối đa 500 ký tự)' }
                        ]}
                    >
                        <Input placeholder="VD: Bệnh nhân ĐK khám chưa có mã nhân viên phụ trách" />
                    </Form.Item>

                    <Space style={{ width: '100%' }} align="start" size={16}>
                        <Form.Item
                            label="Loại thông báo"
                            name="maloaitb"
                            style={{ flex: 1, minWidth: 280 }}
                            rules={[{ required: true, message: 'Vui lòng chọn loại thông báo' }]}
                        >
                            <Select
                                placeholder="-- Chọn loại thông báo --"
                                loading={dmLoading}
                                showSearch
                                optionFilterProp="children"
                            >
                                {loaiTbList.map(l => (
                                    <Option key={l.maloaitb} value={l.maloaitb}>
                                        <Space>
                                            <Text strong>Mã {l.maloaitb}</Text>
                                            <Text>{l.tenloaitb}</Text>
                                            {l.mota && <Text type="secondary" style={{ fontSize: 12 }}>— {l.mota}</Text>}
                                        </Space>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Mức độ nghiêm trọng"
                            name="mamucdo"
                            style={{ flex: 1, minWidth: 280 }}
                            rules={[{ required: true, message: 'Vui lòng chọn mức độ' }]}
                        >
                            <Select
                                placeholder="-- Chọn mức độ --"
                                loading={dmLoading}
                                showSearch
                                optionFilterProp="children"
                            >
                                {mucDoList.map(m => (
                                    <Option key={m.mamucdo} value={m.mamucdo}>
                                        <Space>
                                            <Tag color={tagColorForMucDo(m.colorCode)}>{m.tenmucdo || `Mã ${m.mamucdo}`}</Tag>
                                            {m.colorCode && <Text code type="secondary" style={{ fontSize: 12 }}>{m.colorCode}</Text>}
                                        </Space>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Kích hoạt"
                            name="active"
                            valuePropName="checked"
                            style={{ minWidth: 120 }}
                        >
                            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                        </Form.Item>
                    </Space>

                    <Form.Item
                        label={
                            <Space>
                                SQL truy vấn HIS (ReportDB)
                                <Tag color="blue">phải bắt đầu DECLARE / SELECT / WITH</Tag>
                            </Space>
                        }
                        name="sqlQuery"
                        rules={[
                            { required: true, message: 'Vui lòng nhập SQL truy vấn' },
                            {
                                validator: (_, value) => {
                                    const v = String(value || '').trim().toLowerCase();
                                    if (!v) return Promise.resolve();
                                    if (v.startsWith('declare') || v.startsWith('select') || v.startsWith('with')) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('SQL phải bắt đầu bằng DECLARE, SELECT hoặc WITH (chống chỉnh sửa CSDL HIS).'));
                                }
                            }
                        ]}
                        extra={
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Khuyến nghị: SELECT các cột <Text code>makcb, manv, makk, noidung, ngay, tendichvu, manhom, ngaykt</Text>. Các cột khác sẽ được bỏ qua khi ghi vào bảng notifications. Bắt buộc dùng <Text code>WITH (NOLOCK)</Text> khi truy vấn bảng HIS transaction lớn (thanhtoan, dangky, ...).
                            </Text>
                        }
                    >
                        <TextArea
                            rows={10}
                            placeholder={
                                "-- Ví dụ: Bệnh nhân đăng ký trong ngày chưa có nhân viên phụ trách (manv null)\n" +
                                "DECLARE @tuNgay DATE = CAST(GETDATE() AS DATE)\n" +
                                "DECLARE @denNgay DATE = DATEADD(DAY, 1, @tuNgay)\n" +
                                "SELECT \n" +
                                "  d.makcb,\n" +
                                "  null AS manv,\n" +
                                "  d.makk,\n" +
                                "  N'BN ' + d.hoten + N' đăng ký ngày ' + CONVERT(nvarchar, d.NGAY, 103) + N' chưa có Bác sĩ phụ trách' AS noidung,\n" +
                                "  d.NGAY AS ngay,\n" +
                                "  d.tendichvu AS tendichvu,\n" +
                                "  d.manhom AS manhom,\n" +
                                "  d.ngaykt AS ngaykt\n" +
                                "FROM dangky d WITH (NOLOCK)\n" +
                                "WHERE d.NGAY >= @tuNgay AND d.NGAY < @denNgay\n" +
                                "  AND (d.manv IS NULL OR LTRIM(RTRIM(d.manv)) = '')"
                            }
                            style={{ fontFamily: 'Consolas, monospace', fontSize: 13, lineHeight: 1.5 }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};

export default RuleCheckTab;
